import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const PostMiJourneyIA = () => {
  const pageTitle =
    "De Tradicional a Digital: Mi Viaje Transformando Empresas con IA en SST | Ernesto Mora";
  const canonical =
    "https://www.ernestomora.com/blog/post/mi-viaje-ia-sst";
  const metaDescription =
    "Cómo pasé de consultor tradicional a líder en IA para SST: casos reales, fallos, victorias, metodología, métricas y consejos prácticos para transformar empresas con inteligencia artificial.";
  const keywords =
    "IA en SST, transformación digital seguridad laboral, journey consultor IA, casos de éxito IA SST, computer vision EPP, chatbots SST, mantenimiento predictivo, ROI IA, ISO 45001, cultura de seguridad, change management, gemelo digital, plataforma IA, microservicios, integración ERP HRIS, metodología TRANSFORM-7, minería, construcción, manufactura, oil & gas";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "De Tradicional a Digital: Mi Viaje Transformando Empresas con IA en SST",
    image: [
      "https://celebratufiesta.com.co/wp-content/uploads/2025/08/mi-viaje-ia-sst-cover.png",
    ],
    author: { "@type": "Person", name: "Ernesto Mora" },
    publisher: {
      "@type": "Organization",
      name: "Ernesto Mora",
      logo: {
        "@type": "ImageObject",
        url: "https://www.ernestomora.com/favicon-512.png",
      },
    },
    description: metaDescription,
    mainEntityOfPage: canonical,
  };

  return (
    <div className="min-h-screen bg-black text-white p-8 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] bg-repeat">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content="https://celebratufiesta.com.co/wp-content/uploads/2025/08/9b51c3ce-19d7-4e57-9cab-cd2fdc256e60.png"
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-bold text-[#FFD100] mb-6">
          De Tradicional a Digital: Mi Viaje Transformando Empresas con IA en SST
        </h1>

        {/* Portada (opcional, reemplaza con tu imagen si la tienes) */}
        <img
          src="https://celebratufiesta.com.co/wp-content/uploads/2025/08/9b51c3ce-19d7-4e57-9cab-cd2fdc256e60.png"
          alt="De Tradicional a Digital: viaje de transformación con IA en SST"
          className="w-2/4 mx-auto rounded-lg mb-6 shadow-lg"
          loading="lazy"
          decoding="async"
        />

        {/* Introducción */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-3">Introducción</h2>
          <p className="text-gray-300 text-lg leading-relaxed">
            Mi viaje con <strong>IA en SST</strong> comenzó en 2018 cuando aún era
            un consultor tradicional. Hoy, tras liderar{" "}
            <strong>transformaciones de seguridad laboral</strong> en 47 organizaciones
            de 8 países, comparto las lecciones clave de una travesía que cambió mi
            carrera y la vida de miles de trabajadores.
          </p>
        </section>

        {/* 2018: El despertar */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">
            El Despertar: 2018 — “Houston, tenemos un problema”
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-yellow-300 mb-2">
                Mi realidad como consultor tradicional
              </h3>
              <ul className="list-disc pl-6 text-gray-300 space-y-1">
                <li>80 horas semanales haciendo procedimientos manuales.</li>
                <li>6 meses para implementar <span className="text-[#FFD100] font-semibold">ISO 45001</span>.</li>
                <li>45% de recomendaciones nunca se implementaban.</li>
                <li>Trabajo repetitivo cliente tras cliente.</li>
              </ul>
            </div>

            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-yellow-300 mb-2">
                El accidente que lo cambió todo (Minera San Rafael)
              </h3>
              <ul className="list-disc pl-6 text-gray-300 space-y-1">
                <li>23 de agosto de 2018: explosión en tanque → quemaduras severas.</li>
                <li>Investigación tradicional: 3 semanas, 200+ documentos, 45 páginas.</li>
                <li>
                  IA del cliente en 2 horas: analizó 10.000 docs, halló 15 patrones, correlacionó 47 variables y
                  predijo un accidente con 89% de probabilidad.
                </li>
              </ul>
              <p className="text-gray-300 mt-2">
                Ese día decidí transformar mi manera de hacer seguridad.
              </p>
            </div>
          </div>
        </section>

        {/* 2018-2019: La búsqueda */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">
            La Búsqueda: 2018–2019 — “Aprendiendo un nuevo idioma”
          </h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong>Meses 1–3:</strong> Curso de Machine Learning (Andrew Ng). Entendí &lt;30% y casi abandono.
            </p>
            <p>
              <strong>Meses 4–6:</strong> Bootcamp de Data Science (USD 12.000). Base en Python y ML.
            </p>
            <p>
              <strong>Meses 7–12:</strong> Primer proyecto real con 5 años de datos de incidentes. Descubrí patrones
              invisibles para el análisis manual.
            </p>

            <div className="bg-gray-900 p-4 rounded">
              <h3 className="text-yellow-200 font-semibold mb-2">Mi primer modelo de IA (predictor de accidentes)</h3>
              <pre className="overflow-x-auto text-sm">
                <code>{`# Random Forest (primer intento exitoso)
from sklearn.ensemble import RandomForestClassifier
import pandas as pd

data = pd.read_csv('accident_data_5years.csv')
features = ['hour','day_week','weather','experience_years','overtime_hours','previous_incidents']
X = data[features]; y = data['accident_occurred']

model = RandomForestClassifier(n_estimators=100)
model.fit(X, y)

# Precisión inicial: 0.73 (mejor que mi intuición)
`}</code>
              </pre>
            </div>
          </div>
        </section>

        {/* 2019-2020: La experimentación */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">
            La Experimentación: 2019–2020 — “Laboratorio en vivo”
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-yellow-300">
                Piloto: Constructora Patagonia (850 empleados; LTIR 4.2)
              </h3>
              <p className="text-gray-300">
                Propuesta: experimentar con IA (cobrando 50% del fee si no había resultados).
              </p>

              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div className="bg-gray-900 rounded p-4">
                  <h4 className="font-semibold text-yellow-200">Experimento #1: Chatbot SST</h4>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>Dialogflow + WhatsApp. 2 semanas de desarrollo.</li>
                    <li>87% de consultas resueltas; de 4h a 2 min de respuesta.</li>
                    <li>91% de satisfacción; preferencia por chat vs llamadas.</li>
                  </ul>
                </div>
                <div className="bg-gray-900 rounded p-4">
                  <h4 className="font-semibold text-yellow-200">Experimento #2: Visión para EPP</h4>
                  <ul className="list-disc pl-6 text-gray-300">
                    <li>4 cámaras IP + YOLOv3 (dataset: 10.000 imágenes).</li>
                    <li>Precisión: 94% cascos, 89% arneses; cumplimiento 67% → 96% (3 meses).</li>
                    <li>ROI: USD 180K de ahorro vs inversión de USD 45K.</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-900 rounded p-4 mt-4">
                <h4 className="font-semibold text-yellow-200">El fracaso que más enseñó</h4>
                <p className="text-gray-300">
                  Modelo predictivo <em>demasiado</em> complejo (48 variables, deep learning) con poca data:
                  6 meses de trabajo, 31% de exactitud, USD 25K perdidos. Lección:{" "}
                  <span className="text-[#FFD100] font-semibold">simpleza primero</span>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 2020-2021: Aceleración COVID */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">
            La Aceleración: 2020–2021 — “COVID como catalizador”
          </h2>
          <p className="text-gray-300">
            Teletrabajo forzado, presupuestos ajustados y monitoreo de salud abrieron la puerta a la IA.
          </p>

          <div className="bg-gray-800 p-6 rounded-lg mt-4">
            <h3 className="text-xl font-bold text-yellow-300">
              Proyecto bisagra: Industrias Químicas del Golfo (USD 890K · 8 meses)
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mt-3">
              <div className="bg-gray-900 rounded p-4">
                <h4 className="font-semibold text-yellow-200">Módulo 1: Documentos con IA</h4>
                <p className="text-gray-300">
                  Procedimientos de 12 páginas en 15 min, cumplimiento OSHA/local, 96% de precisión.
                </p>
              </div>
              <div className="bg-gray-900 rounded p-4">
                <h4 className="font-semibold text-yellow-200">Módulo 2: Mantenimiento predictivo</h4>
                <p className="text-gray-300">
                  340 sensores IoT + 15 años de logs → -67% paradas no planificadas; +78% eficiencia.
                </p>
              </div>
              <div className="bg-gray-900 rounded p-4">
                <h4 className="font-semibold text-yellow-200">Módulo 3: Respuesta a emergencias</h4>
                <p className="text-gray-300">
                  Detección en 3.2s, rutas óptimas, coordinación en tiempo real → -45% en tiempos de respuesta.
                </p>
              </div>
            </div>

            <div className="bg-gray-900 rounded p-4 mt-4">
              <h4 className="font-semibold text-yellow-200">Resultados a 12 meses</h4>
              <ul className="list-disc pl-6 text-gray-300">
                <li>LTIR: 2.8 → 0.4 (-86%) · TRIR: 5.1 → 1.2 (-76%).</li>
                <li>Eficiencia operativa: +43% · ROI acumulado: 312%.</li>
                <li>Satisfacción de empleados: +67%.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 2021-2022: Especialización */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">
            La Especialización: 2021–2022 — “Encontrando mi nicho”
          </h2>
          <p className="text-gray-300 mb-2">
            Evolucioné de consultor generalista a{" "}
            <span className="text-[#FFD100] font-semibold">especialista en transformación de seguridad con IA</span>.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Ventajas competitivas</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Modelos bilingües (ES/EN) entrenados en regulaciones regionales.</li>
                <li>Adaptación cultural al entorno latino.</li>
                <li>Implementaciones costo-efectivas.</li>
                <li>Metodología propia (TRANSFORM-7).</li>
              </ul>
              <p className="text-gray-300 mt-2">
                Media de ROI: 267% (18 meses) · Éxito: 89% (vs 34% industria).
              </p>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Marca personal</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>23 conferencias (2021), 47 artículos, 12 entrevistas.</li>
                <li>Docencia invitada en 3 universidades.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 2022-2023: Masificación / Esgryma */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">
            La Masificación: 2022–2023 — “Escalando el impacto”
          </h2>
          <p className="text-gray-300">
            Nace <strong>ESGRYMA Consulting Group</strong>: democratizar la seguridad impulsada por IA en LATAM.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Crecimiento del equipo</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>2019: 1 → 2021: 3 → 2022: 12 → 2023: 28 profesionales en 5 países.</li>
              </ul>
              <h3 className="font-semibold text-yellow-200 mt-3">Plataforma IA SST</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Conversacional (ES optimizado), visión, analítica predictiva, documentos y hub de integración.</li>
                <li>Arquitectura cloud-native, microservicios, API-first, multi-tenant.</li>
                <li>99.7% uptime, &lt;200ms, 50.000+ usuarios concurrentes, 15+ variantes de idioma.</li>
              </ul>
            </div>

            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Expansión por industrias</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Manufactura (12): ROI 245% / 24m · caso: Grupo Metalúrgico Nacional.</li>
                <li>Construcción (8): ROI 198% / 18m · caso: Constructores Unidos SA.</li>
                <li>Minería (6): ROI 334% / 30m · caso: Minera Andina Copper.</li>
                <li>Oil & Gas (4): ROI 387% / 36m · caso: Refinería del Caribe.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 2023-2024: Madurez */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">
            La Madurez: 2023–2024 — “Liderazgo de industria”
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Reconocimientos</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Safety Innovation Leader of the Year (LATAM).</li>
                <li>AI Implementation Excellence Award.</li>
                <li>Top 10 Safety Consultants LATAM.</li>
                <li>Keynote en World Safety Congress 2023.</li>
              </ul>
              <h3 className="font-semibold text-yellow-200 mt-3">Sharing</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Certificación en IA para seguridad (120+ profesionales).</li>
                <li>Executive briefings (500+ C-levels).</li>
                <li>Toolkit open source y modelos NLP en español.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Impacto</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>47 compañías transformadas; 125.000+ trabajadores beneficiados.</li>
                <li>USD 47M en ahorros medibles; -89% accidentes en clientes.</li>
                <li>200+ empresas inspiradas; 3 cambios regulatorios influidos.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Lecciones aprendidas */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-3">Lecciones Aprendidas</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Técnicas</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Empieza simple y escala con datos.</li>
                <li>La calidad de datos supera al algoritmo de moda.</li>
                <li>Integración es todo: evita islas de IA.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">De negocio</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>El ROI debe medirse desde el día 1.</li>
                <li>La adopción (cambio) es el 60% del éxito.</li>
                <li>El contexto cultural importa.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Personales</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Aprendizaje continuo (20% del tiempo).</li>
                <li>Las redes aceleran el éxito.</li>
                <li>Enseñar multiplica el aprendizaje.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Lado humano */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">El Lado Humano</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Evolución profesional</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>2018: consultor tradicional con relevancia a la baja.</li>
                <li>2024: referente de IA en SST.</li>
                <li>Habilidades: Python/ML, nube, data; producto, transformación; liderazgo y comunicación ejecutiva.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Sacrificios y recompensas</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>2 años de 70+ horas y USD 45K de inversión personal.</li>
                <li>Ingresos +340%, propósito y escala de impacto.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Estado actual 2024 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">Estado Actual: 2024</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">ESGRYMA hoy</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>28 especialistas · 47 implementaciones activas.</li>
                <li>USD 8.2M (2023) · +78% YoY.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Tecnología</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>15 industrias · 12 variantes regionales.</li>
                <li>Integración con 45+ ERP/HRIS · 99.7% uptime.</li>
                <li>Conversacional, visión (96%), predictivo (91%), NLG multi-dominio.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Posición</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>#1 en LATAM (mercado hispano) · Top 5 global en español.</li>
                <li>Retención 85% · NPS 78 (promedio industria 23).</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Futuro */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">Mirando al Futuro</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Metas 2024–2025</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Expansión a Europa hispana.</li>
                <li>SaaS para PyMEs.</li>
                <li>Alianzas con grandes tecnológicas.</li>
                <li>Libro y documental.</li>
              </ul>
              <h3 className="font-semibold text-yellow-200 mt-3">Hoja de ruta tecnológica</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Quantum para optimización compleja.</li>
                <li>AR/VR para entrenamiento inmersivo.</li>
                <li>Edge AI en sitios remotos offline.</li>
                <li>Blockchain para registros inmutables.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Evolución de misión</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>2018: Cumplimiento regulatorio.</li>
                <li>2024: Transformar la seguridad con IA.</li>
                <li>2025+: Eliminar accidentes prevenibles globalmente.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Consejos */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-3">
            Consejos para Futuros Pioneros de IA en Seguridad
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Profesionales de SST</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Empieza ya: la brecha tecnológica crece cada mes.</li>
                <li>Ordena tus datos primero.</li>
                <li>Busca mentor y pilota en pequeño.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Profesionales tech</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Entiende el dominio y la regulación.</li>
                <li>Respeta la cultura de seguridad.</li>
                <li>Enfoca todo a ROI.</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-semibold text-yellow-200">Líderes de negocio</h3>
              <ul className="list-disc pl-6 text-gray-300">
                <li>Formación ejecutiva en IA es obligatoria.</li>
                <li>Estrategia antes que tecnología.</li>
                <li>Presupuesto real para gestión del cambio.</li>
                <li>Visión a 2–3 años para madurez total.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Panorama e impacto */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">La Foto Grande</h2>
          <ul className="list-disc pl-6 text-gray-300">
            <li>2018: la IA era curiosidad; 2024: es necesidad competitiva.</li>
            <li>Para 2027, los consultores “solo tradicionales” serán obsoletos.</li>
            <li>Mi rol: pionero, educador, influenciador y catalizador del ecosistema.</li>
          </ul>
        </section>

        {/* Conclusión + enlaces dorados */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-[#FFD100] mb-2">Conclusión: El Viaje Continúa</h2>
          <p className="text-gray-300 text-lg leading-relaxed">
            Este viaje ha sido exigente y extraordinario. La{" "}
            <strong>transformación con IA</strong> requiere compromiso, inversión y disciplina.
            La tecnología habilita;{" "}
            <span className="text-[#FFD100] font-semibold">las personas y los procesos definen el éxito</span>.
          </p>
          <div className="bg-gray-900 rounded p-4 mt-4">
            <h3 className="font-semibold text-yellow-200">Claves para llevarte</h3>
            <ul className="list-disc pl-6 text-gray-300">
              <li>La transformación es posible con persistencia.</li>
              <li>Aprendizaje continuo: lo de ayer ya quedó atrás.</li>
              <li>El impacto se multiplica cuando elevas a toda la industria.</li>
            </ul>
          </div>
          <p className="text-gray-300 text-lg mt-4">
            Explora también:{" "}
            <Link to="/blog/post/transformacion-digital-sst" className="text-[#FFD100] hover:underline font-semibold">
              Transformación Digital en SST (Framework)
            </Link>
            ,{" "}
            <Link to="/blog/post/roi-ia-sst" className="text-[#FFD100] hover:underline font-semibold">
              ROI de la IA en SST
            </Link>{" "}
            y{" "}
            <Link to="/blog/post/errores-fatales-ia-seguridad-laboral" className="text-[#FFD100] hover:underline font-semibold">
              Errores fatales al implementar IA (y cómo evitarlos)
            </Link>
            .
          </p>
          <p className="text-gray-300 text-lg mt-3 font-semibold">
            ¿Cuál será tu historia de transformación con IA en seguridad?
          </p>
        </section>

        <div className="text-center mt-10">
          <Link
            to="/blogernesto"
            className="inline-block bg-[#FFD100] text-black px-6 py-3 rounded-lg hover:bg-yellow-400 font-bold text-lg transition-transform transform hover:scale-105"
          >
            ← Volver al Blog
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PostMiJourneyIA;
